export type EvvStatus = "pending" | "submitted" | "error";

export interface EvvStatusInfo {
  status: EvvStatus;
  tooltip: string;
  color: string;
}

/**
 * EVV submission details types (matches backend GraphQL schema)
 * Using 'any' for branded types (Instant) to allow flexibility with GraphQL generated types
 */
export interface EmednyVisitSubmission {
  transactionId: string;
  submitted: boolean;
  processedAt: any; // Instant type
  errorType: string | null;
  errors: (string | null)[] | null;
}

export interface TellusVisitSubmission {
  createdAt: any; // Instant type
  submitted: boolean;
  errorCodes: (string | null)[] | null;
}

export interface SandataVisitSubmission {
  submittedAt: any; // Instant type
  completed: boolean | null;
  errors: (string | null)[] | null;
}

export interface HHAExchangeVisitSubmission {
  submitted: boolean;
  sentAt: any | null; // Instant type
  errorType: string | null;
  errorMessage: string | null;
  transactionId: string | null;
}

/**
 * Determines EVV status based on actual submission details from the server
 *
 * Logic (in order):
 * - If no EVV aggregator: not applicable (gray)
 * - If a submission row exists for the configured aggregator: dispatch to its handler
 *     (errors → red, submitted → green, in-flight → orange)
 * - Else if visit not completed: pending (orange) — nothing to submit yet
 * - Else: pending (gray) — visit done, waiting for the submission worker
 */
export function getEvvStatus(
  clockinTime: string | null,
  clockoutTime: string | null,
  evvAggregator: string | null,
  emednySubmission: EmednyVisitSubmission | null,
  tellusSubmission: TellusVisitSubmission | null,
  sandataSubmission: SandataVisitSubmission | null,
  hhaexchangeSubmission: HHAExchangeVisitSubmission | null
): EvvStatusInfo {
  // If no EVV aggregator configured, not applicable
  if (!evvAggregator) {
    return {
      status: "pending",
      tooltip: "EVV not configured for this contract",
      color: "gray.500",
    };
  }

  // Determine which submission to check based on aggregator
  const aggregatorLower = evvAggregator.toLowerCase();

  // If a submission row exists, it's authoritative — render its real state
  // even if the visit is missing clockin/clockout (worker has already acted).
  if (aggregatorLower === "emedny" && emednySubmission) {
    return getEmednyStatus(emednySubmission);
  }
  if (aggregatorLower === "tellus" && tellusSubmission) {
    return getTellusStatus(tellusSubmission);
  }
  if (aggregatorLower === "sandata" && sandataSubmission) {
    return getSandataStatus(sandataSubmission);
  }
  if (
    (aggregatorLower === "hhaexchange - sftp" || aggregatorLower === "hhaexchange - api") &&
    hhaexchangeSubmission
  ) {
    return getHHAExchangeStatus(hhaexchangeSubmission);
  }

  // No submission yet — fall back to visit-state messages.
  if (!clockinTime || !clockoutTime) {
    return {
      status: "pending",
      tooltip: "Pending: Visit not completed",
      color: "orange.500",
    };
  }

  return {
    status: "pending",
    tooltip: `Pending: Waiting for ${getEvvAggregatorDisplayName(evvAggregator)} submission`,
    color: "gray.500",
  };
}

function getEmednyStatus(submission: EmednyVisitSubmission): EvvStatusInfo {
  // Check for errors
  if (submission.errorType && submission.errors && submission.errors.length > 0) {
    const errorType = submission.errorType.replace(/_/g, " ");
    const errorMessages = submission.errors
      .filter((e): e is string => e !== null)
      .map((e) => `• ${e}`)
      .join("\n");

    let tooltip = `eMedny Submission Error\n\nError Type: ${errorType}\n\nErrors:\n${errorMessages}`;
    tooltip += `\n\nTransaction ID: ${submission.transactionId}`;
    if (submission.processedAt) {
      const processedDate = new Date(submission.processedAt).toLocaleString();
      tooltip += `\nProcessed: ${processedDate}`;
    }

    return {
      status: "error",
      tooltip,
      color: "red.500",
    };
  }

  // Check submission status
  if (submission.submitted) {
    let tooltip = `Successfully submitted to eMedny`;
    tooltip += `\n\nTransaction ID: ${submission.transactionId}`;
    if (submission.processedAt) {
      const processedDate = new Date(submission.processedAt).toLocaleString();
      tooltip += `\nProcessed: ${processedDate}`;
    }

    return {
      status: "submitted",
      tooltip,
      color: "green.500",
    };
  }

  // Not yet submitted (no errors, but not submitted either)
  let tooltip = `Pending: Processing eMedny submission`;
  tooltip += `\n\nTransaction ID: ${submission.transactionId}`;
  if (submission.processedAt) {
    const processedDate = new Date(submission.processedAt).toLocaleString();
    tooltip += `\nProcessed: ${processedDate}`;
  }

  return {
    status: "pending",
    tooltip,
    color: "orange.500",
  };
}

function getTellusStatus(submission: TellusVisitSubmission): EvvStatusInfo {
  // Check for errors
  if (submission.errorCodes && submission.errorCodes.length > 0) {
    const errorMessages = submission.errorCodes
      .filter((e): e is string => e !== null)
      .map((e) => `• ${e}`)
      .join("\n");

    return {
      status: "error",
      tooltip: `Tellus Submission Error\n\nErrors:\n${errorMessages}`,
      color: "red.500",
    };
  }

  // Check submission status
  if (submission.submitted) {
    return {
      status: "submitted",
      tooltip: "Successfully submitted to Tellus",
      color: "green.500",
    };
  }

  return {
    status: "pending",
    tooltip: "Pending: Processing Tellus submission",
    color: "orange.500",
  };
}

function getSandataStatus(submission: SandataVisitSubmission): EvvStatusInfo {
  // Check for errors
  if (submission.errors && submission.errors.length > 0) {
    const errorMessages = submission.errors.filter((e): e is string => e !== null).join(", ");

    return {
      status: "error",
      tooltip: `Sandata Submission Error\n\nErrors:\n${errorMessages}`,
      color: "red.500",
    };
  }

  // Check if submitted and completed
  if (submission.submittedAt && submission.completed) {
    return {
      status: "submitted",
      tooltip: "Successfully submitted to Sandata",
      color: "green.500",
    };
  }

  // Pending submission

  return {
    status: "pending",
    tooltip: "Pending: Processing Sandata submission",
    color: "orange.500",
  };
}

function getHHAExchangeStatus(submission: HHAExchangeVisitSubmission): EvvStatusInfo {
  const sentAtStr = submission.sentAt
    ? `\nSent: ${new Date(submission.sentAt).toLocaleString()}`
    : "";
  const transactionIdStr = submission.transactionId
    ? `\nTransaction ID: ${submission.transactionId}`
    : "";

  // Check for errors
  if (submission.errorType !== null) {
    const errorType = submission.errorType.replace(/_/g, " ");
    const errorLine = submission.errorMessage ? `\n\nErrors:\n• ${submission.errorMessage}` : "";

    return {
      status: "error",
      tooltip: `HHAExchange Submission Error\n\nError Type: ${errorType}${errorLine}${transactionIdStr}${sentAtStr}`,
      color: "red.500",
    };
  }

  // Check submission status
  if (submission.submitted) {
    return {
      status: "submitted",
      tooltip: `Successfully submitted to HHAExchange${transactionIdStr}${sentAtStr}`,
      color: "green.500",
    };
  }

  return {
    status: "pending",
    tooltip: `Pending: Processing HHAExchange submission${transactionIdStr}`,
    color: "orange.500",
  };
}

/**
 * Gets the display text for EVV aggregator
 */
export function getEvvAggregatorDisplayName(aggregator: string | null): string {
  if (!aggregator) {
    return "Unknown";
  }

  const aggregatorMap: Record<string, string> = {
    sandata: "Sandata",
    tellus: "Tellus",
    emedny: "eMedny",
    "hhaexchange - sftp": "HHAExchange - SFTP",
    "hhaexchange - api": "HHAExchange - API",
  };

  return aggregatorMap[aggregator.toLowerCase()] || aggregator;
}

/**
 * When a visit was last sent to its aggregator, from whichever submission it has.
 * The Clock In & Out (New) page uses the same precedence for its "Last Submitted At".
 */
export function getEvvLastSubmittedAt(submissions: {
  emednySubmission?: { processedAt: any } | null;
  tellusSubmission?: { createdAt: any } | null;
  sandataSubmission?: { submittedAt: any } | null;
  hhaexchangeSubmission?: { sentAt: any | null } | null;
}): any | null {
  return (
    submissions.emednySubmission?.processedAt ??
    submissions.tellusSubmission?.createdAt ??
    submissions.sandataSubmission?.submittedAt ??
    submissions.hhaexchangeSubmission?.sentAt ??
    null
  );
}

/** The error text a visit's current submission carries, whichever aggregator it went to. */
export function getEvvCurrentErrors(submissions: {
  emednySubmission?: { errors: (string | null)[] | null } | null;
  tellusSubmission?: { errorCodes: (string | null)[] | null } | null;
  sandataSubmission?: { errors: (string | null)[] | null } | null;
  hhaexchangeSubmission?: { errorMessage: string | null } | null;
}): string[] {
  const lines = [
    ...(submissions.emednySubmission?.errors ?? []),
    ...(submissions.tellusSubmission?.errorCodes ?? []),
    ...(submissions.sandataSubmission?.errors ?? []),
    submissions.hhaexchangeSubmission?.errorMessage ?? null,
  ];
  return lines.filter((line): line is string => line !== null && line !== "");
}
